import React, { forwardRef, useState } from 'react';
import BaseGrid from 'components/atoms/base-grid';
import BaseTextArea from 'components/atoms/base-textarea';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ChevronBottom from 'components/atoms/icons/chevron-bottom';
import ChevronRight from 'components/atoms/icons/chevron-right';
import ChainIcon from 'components/atoms/icons/chainIcon';
import CopyIcon from 'components/atoms/icons/copyIcon';
import { copyTextWithResult } from 'utils/helpers/copy-text-with-result';
import { cropText } from 'infrastructure/functions';

import s from './styles.module.scss';
import CheckboxItem from './checkboxItem';
import DateItem from './dateItem';
import RadioLineItem from './radioLineItem';
import UncheckableRadioItem from './uncheckableRadioItem';

import type { IFormItemProps } from './types';

const FormItem = forwardRef<HTMLDivElement, IFormItemProps>((props, ref) => {
  const { formik, formItem, modalType, children, requiredSymptomFields } =
    props;

  const {
    title,
    description,
    type,
    required,
    requiredIf,
    options,
    name: generalName,
    readingTypeFor,
    extra,
    link,
    iconLink,
    icon: Icon,
    alignCenter,
    infoDescription,
    infoIconLink,
    infoIcon: InfoIcon,
    headerCollapse,
    headerIconLink,
    headerIcon: HeaderIcon,
  } = formItem;

  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const isExtraFieldError =
    Object.keys(formik.errors).some((k) => extra?.some((e) => e?.name === k)) ||
    Object.keys(formik.errors).some((k) =>
      extra?.some((e) => e.options?.some((eo) => eo?.value === k)),
    );
  const isExtraExtraFieldError =
    Object.keys(formik.errors).some((k) =>
      extra?.some((e) => e.extra?.some((i) => i?.name === k)),
    ) ||
    Object.keys(formik.errors).some((k) =>
      extra?.some((e) =>
        e.extra?.some((i) => i.options?.some((eo) => eo?.value === k)),
      ),
    );
  const isOptionsError = Object.keys(formik.errors).some((k) =>
    options
      ?.filter((o) => o.required)
      .map((i) => i.value)
      .includes(k),
  );
  const isShowErrorMessage =
    (Object.keys(formik.errors).includes(generalName) && required) ||
    isOptionsError ||
    isExtraFieldError ||
    isExtraExtraFieldError;

  const itemClassNames = classNames(s['form-item'], {
    [s.error]: !!Object.keys(formik.errors).length && isShowErrorMessage,
  });

  const { t } = useTranslation();

  const bodyClassNames = classNames(s.body, {
    [s['flex-body']]: formItem.flexBlock,
  });
  const headerClassNames = classNames({
    [s.header]:
      type !== 'radio-list' &&
      title !== '' &&
      type !== 'info' &&
      !headerCollapse,
    [s.divider]: type !== 'text',
  });

  if (!(modalType && readingTypeFor.includes(modalType))) return null;

  return (
    <div
      className={itemClassNames}
      ref={generalName === 'nursingIntervention' ? ref : null}
    >
      <div className={headerClassNames}>
        {headerCollapse !== true && title !== '' && type !== 'info' && (
          <div className={s.title}>
            {title}
            {required && <span>*</span>}
            {requiredIf &&
              requiredIf.key &&
              requiredIf.value ===
                formik.getFieldMeta(requiredIf.key).value && <span>*</span>}
          </div>
        )}
        {headerCollapse === true && title !== '' && type !== 'info' && (
          <div className={[s.title, s['header-collapse']].join(' ')}>
            <div>
              {headerIconLink && <img src={headerIconLink} alt="header icon" />}
              {HeaderIcon && <HeaderIcon />}
              <span>
                {title}
                {required && <span>*</span>}
                {requiredIf &&
                  requiredIf.key &&
                  requiredIf.value ===
                    formik.getFieldMeta(requiredIf.key).value && <span>*</span>}
              </span>
            </div>
            <button onClick={() => setIsCollapseOpen(!isCollapseOpen)}>
              {isCollapseOpen && <ChevronBottom />}
              {!isCollapseOpen && <ChevronRight />}
            </button>
          </div>
        )}

        {description && <div className={s.description}>{description}</div>}
      </div>
      <div
        className={bodyClassNames}
        hidden={!isCollapseOpen && headerCollapse}
      >
        {type === 'info' && (
          <div
            className={
              alignCenter ? [s.info, s['align-center']].join(' ') : s.info
            }
          >
            {Icon && <Icon />}
            {iconLink && <img src={iconLink} alt="info icon" />}
            <span>{title}</span>
          </div>
        )}
        {infoDescription && (
          <div
            className={
              alignCenter
                ? [s['info-description'], s['align-center']].join(' ')
                : s['info-description']
            }
          >
            {InfoIcon && <InfoIcon />}
            {infoIconLink && <img src={infoIconLink} alt="info icon" />}
            <span>{infoDescription}</span>
          </div>
        )}
        {type === 'link' && link && (
          <div className={s.link}>
            <div>
              <ChainIcon />
              <a href={link} target="_blank">
                {cropText(link, 36)}
              </a>
            </div>
            <button onClick={() => copyTextWithResult(link)}>
              <CopyIcon />
              <span>{t('controls.copyLink')}</span>
            </button>
          </div>
        )}
        {type === 'checkbox' &&
          options &&
          options.map((option) => (
            <CheckboxItem
              option={option}
              formik={formik}
              generalName={generalName}
            />
          ))}

        {type === 'datepicker' && options && (
          <BaseGrid columns={2} className={s['item-row']}>
            {options.map((option) => {
              return (
                <DateItem option={option} formik={formik} key={option.key} />
              );
            })}
          </BaseGrid>
        )}

        {type === 'radio' && options && (
          <UncheckableRadioItem
            options={options}
            name={generalName}
            value={formik.values[generalName]}
            formik={formik}
          />
        )}

        {type === 'text' && (
          <BaseTextArea
            rows={6}
            placeholder="Type here details"
            onChange={(value) => formik.setFieldValue(generalName, value)}
            value={formik.values[generalName]}
          />
        )}

        {type === 'radio-list' && options && (
          <div className={s['radio-wrapper']}>
            {options.map((option) => (
              <RadioLineItem
                option={option}
                formik={formik}
                generalName={generalName}
                requiredSymptomFields={requiredSymptomFields}
                key={option.key}
              />
            ))}
          </div>
        )}

        {type === 'list' && options && (
          <ul className={s['list-wrapper']}>
            {options.map((option) => (
              <li key={option.key}>{option.label}</li>
            ))}
          </ul>
        )}

        {children}
      </div>
      {isShowErrorMessage && (
        <span className={s['error-block']}>
          {isExtraFieldError ? '' : t('labels.requiredQuestion')}
        </span>
      )}
    </div>
  );
});

export default FormItem;
